import { z } from 'zod';
import { zStringRequired } from '../../z';
import { sectionHasValue } from '../util';
import { baseBloodPressureRefined } from '../oasis-sections/vitals';
import { basePainRefined } from '../shared/pain';
import { baseMusculoskeletalRefined } from '../oasis-sections/section-g';
import { baseGenitourinarySchema } from '../shared/genitourinary';
import { basePsychosocialSchemaRefined } from '../shared/psychosocial';
import { baseNeurologicalSchemaRefined } from '../shared/neurological';
import { baseIntegumentaryRefined } from '../shared/integumentary';
import { baseCardiovascularSchemaRefined } from '../shared/cardiovascular';
import { baseGastrointestinalSchema } from '../shared/gastrointestinal';
import { baseLungAuscultationSchemaRefined, baseBreathingPatternSchema, baseCpapBipapSchema, baseMechanicalVentilatorSchema, baseTracheostomySchema, baseRespiratoryTreatmentsSchema, baseSmokingHistorySchema, baseCoughSchema, } from '../shared/respiratory';
import { baseTemperatureRefined, baseO2Refined, baseRespirationRefined, basePulseRefined, baseBloodSugarRefined } from '../shared/vitals';
import { baseEndocrineSchema } from '../shared/endocrine';
import { baseHealthManagementSchema } from '../shared/health-management';
import { baseOstomySchemaRefined } from '../shared/ostomy';
import { basePainAssessmentSchema } from '../shared/pain-assessment';
import { baseLabDrawSchema } from '../shared/labDraw';
import { baseOralIntakeClinical, baseEatingSupport, basePocNutrition, baseK0520Clinical, baseWeightClinicalRefined, } from '../shared/nutrition';
import { registerEnum, registerEnumArray } from '../registered-enum';
import { registry } from '../registry';
export const AI_NARRATIVE_PENDING_SENTINEL = '__AI_NARRATIVE_PENDING__';
export const AI_NARRATIVE_AWAITING_REVIEW_SENTINEL = '__AI_NARRATIVE_AWAITING_REVIEW__';
const AI_NARRATIVE_SENTINELS = [AI_NARRATIVE_PENDING_SENTINEL, AI_NARRATIVE_AWAITING_REVIEW_SENTINEL];
export function isAiNarrativeSentinel(value) {
    return typeof value === 'string' && AI_NARRATIVE_SENTINELS.includes(value);
}
/**
 * Permissive base — all fields optional. Used by chart-context extractors and
 * text generators which must tolerate partial drafts. The strict form-save
 * version (with `.min(1, 'required')` on the four required fields) is applied
 * at the composition site below so form validation still enforces required
 * fields without leaking those constraints into consumer code.
 */
export const baseHomeboundReason = z
    .object({
    homeboundStatusThisVisit: registerEnum([
        { value: 'unchanged', label: 'Continues to meet homebound criteria — status unchanged' },
        { value: 'changed', label: 'Status changed — document below' },
        { value: 'no-longer', label: 'No longer homebound — notify MD / initiate discharge' },
    ], { label: 'Homebound Status This Visit' }).optional(),
    // Checkbox values ARE the label text (legacy pattern) — value must equal label.
    whatChanged: registerEnumArray([
        { value: 'Condition worsened', label: 'Condition worsened' },
        { value: 'Mobility declined', label: 'Mobility declined' },
        { value: 'Mobility improved', label: 'Mobility improved' },
        { value: 'New qualifying condition', label: 'New qualifying condition' },
        { value: 'Effort increased', label: 'Effort increased' },
        { value: 'Effort decreased', label: 'Effort decreased' },
        { value: 'Absence pattern changed', label: 'Absence pattern changed' },
    ], { label: 'What changed' }).optional(),
    dateChangeNoticed: z.string().optional().register(registry, { label: 'Date change noticed' }),
    triggerEvent: registerEnum([
        { value: 'acute-illness', label: 'Acute illness or exacerbation' },
        { value: 'fall-injury', label: 'Fall or injury' },
        { value: 'post-hospitalization', label: 'Post-hospitalization' },
        { value: 'medication-change', label: 'Medication change' },
        { value: 'therapy-progress', label: 'Therapy progress' },
        { value: 'functional-improvement', label: 'Functional improvement' },
        { value: 'other', label: 'Other' },
    ], { label: 'Trigger event' }).optional(),
    changeDescription: z.string().optional().register(registry, { label: 'Describe the change' }),
    lastDayMetCriteria: z.string().optional().register(registry, { label: 'Last day patient met criteria' }),
    reasonNoLongerHomebound: registerEnum([
        { value: 'functional-improvement', label: 'Functional improvement — independent ambulation' },
        { value: 'goals-met', label: 'Goals met' },
        { value: 'patient-refusal', label: 'Patient / family refusal' },
        { value: 'safety-concerns', label: 'Safety concerns' },
        { value: 'other', label: 'Other' },
    ], { label: 'Reason no longer homebound' }).optional(),
    clinicalJustification: z.string().optional().register(registry, { label: 'Clinical justification' }),
    // Checkbox values ARE the label text (legacy pattern) — value must equal label.
    activeQualifyingConditions: registerEnumArray([
        { value: 'Severe SOB / COPD exacerbation', label: 'Severe SOB / COPD exacerbation' },
        { value: 'Severe weakness / debility', label: 'Severe weakness / debility' },
        { value: 'Orthopedic — weight-bearing restriction', label: 'Orthopedic — weight-bearing restriction' },
        { value: 'Open wound', label: 'Open wound' },
        { value: 'Recent surgery', label: 'Recent surgery' },
        { value: 'Neurological impairment', label: 'Neurological impairment' },
        { value: 'Psychiatric', label: 'Psychiatric' },
        { value: 'Terminal illness', label: 'Terminal illness' },
        { value: 'Other', label: 'Other' },
    ], { label: 'Qualifying Condition' }).optional(),
    activeQualifyingConditionsOther: z.string().optional().register(registry, { label: 'Other qualifying condition' }),
    activeMobilityStatus: registerEnumArray([
        { value: 'Wheelchair', label: 'Wheelchair' },
        { value: 'Human assistance', label: 'Human assistance' },
        { value: 'Ambulatory', label: 'Ambulatory' },
        { value: 'Ambulatory with device', label: 'Ambulatory with device' },
        { value: 'Cane', label: 'Cane' },
        { value: 'Walker', label: 'Walker' },
        { value: 'Bedfast', label: 'Bedfast' },
        { value: 'Chairfast', label: 'Chairfast' },
        { value: 'Special transportation', label: 'Special transportation' },
    ], { label: 'Mobility Status' }).optional(),
})
    .register(registry, { label: 'Homebound' });
/**
 * Permissive base — all sub-sections optional. Used by chart-context extractors
 * and the vitals text generator which must tolerate partial drafts. The
 * strict form-save version (with all sub-sections required and refined) is
 * applied at the composition site below.
 */
export const baseSnnVitals = z
    .object({
    bloodPressure: baseBloodPressureRefined.optional(),
    pulse: basePulseRefined.optional(),
    respiration: baseRespirationRefined.optional(),
    o2: baseO2Refined.optional(),
    temperature: baseTemperatureRefined.optional(),
    bloodSugar: baseBloodSugarRefined.optional(),
})
    .register(registry, { label: 'Vitals' });
/**
 * Permissive base — all fields optional. The form-save composition below
 * applies the `.default('yes')` for `patientPresentDuringVisit`; the base
 * version stays optional so chart-context / text-generator consumers don't
 * have to satisfy a defaulted required field.
 */
const patientPresentDuringVisitEnum = registerEnum([
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No — proxy visit' },
], { label: 'Patient Present During Visit?' });
export const baseSnnSubjectiveReport = z
    .object({
    chiefComplaint: z.string().optional().register(registry, { label: "Chief Complaint — Patient's Own Words" }),
    changeStatus: registerEnum([
        { value: 'improved', label: 'Improved' },
        { value: 'no-change', label: 'No change' },
        { value: 'new-problem', label: 'New problem' },
        { value: 'worse', label: 'Worse — specify below' },
        { value: 'emergency-visit', label: 'Emergency visit/hospitalization' },
    ], { label: 'Changes Since Last Visit' }).optional(),
    changeDetails: z.string().optional().register(registry, { label: 'Change Details' }),
    primaryConditionStatus: registerEnum([
        { value: 'resolved', label: 'Resolved' },
        { value: 'controlled-stable', label: 'Controlled — stable' },
        { value: 'exacerbation-partial', label: 'Exacerbation — partially controlled' },
        { value: 'exacerbation-uncontrolled', label: 'Exacerbation — uncontrolled' },
        { value: 'progressing-as-expected', label: 'Progressing as expected' },
    ], { label: 'Primary Condition Status — Current Visit' }).optional(),
    patientComplianceStatus: registerEnum([
        { value: 'full', label: 'Full compliance' },
        { value: 'partial', label: 'Partial' },
        { value: 'non-compliant', label: 'Non-compliant' },
        { value: 'unable-to-comply', label: 'Unable to comply' },
    ], { label: 'Patient Compliance with Plan of Care' }).optional(),
    patientPresentDuringVisit: patientPresentDuringVisitEnum.optional(),
    caregiverPresent: registerEnum([
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'na', label: 'N/A' },
    ], { label: 'Caregiver Present?' }).optional(),
})
    .register(registry, { label: 'Subjective / Patient Report' });
export const baseSnnCoordinationOfCare = z
    .object({
    // Kept as plain strings (not an enum): stored values ARE the full sentence, and
    // chart-context extraction must tolerate free-text/legacy entries. The options
    // here are display metadata for the checkbox UI and read-only views only.
    physicianNotified: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Physician Notification',
        options: [
            {
                value: 'Certifying Physician/ PA/ NP/ CNS notified and verbal order received to visit the patient',
                label: 'Certifying Physician/ PA/ NP/ CNS notified and verbal order received to visit the patient',
            },
            {
                value: 'Certifying Physician/ PA/ NP/ CNS notified and verbal order received approving the POC',
                label: 'Certifying Physician/ PA/ NP/ CNS notified and verbal order received approving the POC',
            },
        ],
    }),
    nextVisitPlans: z.string().optional().register(registry, { label: 'Next Visit Plans' }),
    coordinationDiscipline: registerEnumArray([
        { value: 'MD', label: 'MD - Medical Doctor' },
        { value: 'NP', label: 'NP - Nurse Practioner' },
        { value: 'PA', label: 'PA - Physician Assistant' },
        { value: 'SN', label: 'SN - Skilled Nurse' },
        { value: 'LPN', label: 'LPN - Licensed Practitional Nurse' },
        { value: 'PT', label: 'PT - Physical Therapy' },
        { value: 'OT', label: 'OT - Occupational Therapy' },
        { value: 'SLP', label: 'SLP - Speech Therapy' },
        { value: 'HHA', label: 'HHA - Home Health Aide' },
    ], { label: 'Coordination With' }).optional(),
    coordinationName: z
        .object({
        md: z.string().optional(),
        np: z.string().optional(),
        pa: z.string().optional(),
        sn: z.string().optional(),
        lpn: z.string().optional(),
        pt: z.string().optional(),
        ot: z.string().optional(),
        slp: z.string().optional(),
        hha: z.string().optional(),
    })
        .optional(),
    narrative: z.string().optional().register(registry, { label: 'Narrative' }),
})
    .register(registry, { label: 'Coordination of Care' });
export const baseSnnNutrition = z
    .object({
    weightClinical: baseWeightClinicalRefined,
    oralIntakeClinical: baseOralIntakeClinical.refine((data) => sectionHasValue(data), {
        message: 'Mark as within normal limits if none applies',
        path: [],
    }),
    eatingSupport: baseEatingSupport.refine((data) => sectionHasValue(data), {
        message: 'Mark as within normal limits if none applies',
        path: [],
    }),
    pocNutrition: basePocNutrition,
    nutritionalApproaches: registerEnumArray([
        { value: 'none', label: 'None of the above' },
        { value: 'parenteral', label: 'A. Parenteral/IV feeding' },
        { value: 'feedingTube', label: 'B. Feeding tube' },
        { value: 'mechanicallyAltered', label: 'C. Mechanically altered diet' },
        { value: 'therapeuticDiet', label: 'D. Therapeutic diet' },
    ], { label: 'Nutritional Approaches' }).optional(),
    nutritionalApproachesClinical: baseK0520Clinical.optional(),
})
    .register(registry, { label: 'Nutrition' });
export const baseSnnNeurosensory = baseNeurologicalSchemaRefined
    .extend({
    painAssessment: basePainAssessmentSchema,
    psychosocial: basePsychosocialSchemaRefined,
})
    .register(registry, { label: 'Neurological' });
export const baseSnnCardiovascular = z
    .object({
    cardiovascular: baseCardiovascularSchemaRefined,
})
    .register(registry, { label: 'Cardiovascular' });
export const baseSnnRespiratory = z
    .object({
    smokingHistory: baseSmokingHistorySchema,
    breathingPattern: baseBreathingPatternSchema,
    lungAuscultation: baseLungAuscultationSchemaRefined,
    cough: baseCoughSchema,
    cpapBipap: baseCpapBipapSchema,
    mechanicalVentilator: baseMechanicalVentilatorSchema,
    tracheostomy: baseTracheostomySchema,
    treatments: baseRespiratoryTreatmentsSchema,
})
    .register(registry, { label: 'Respiratory' });
export const baseSnnEndocrine = z
    .object({
    endocrine: baseEndocrineSchema.extend({ changeFromLastVisit: z.string().min(1, 'required') }),
})
    .register(registry, { label: 'Endocrine' });
export const baseSnnBladderBowel = z
    .object({
    gastrointestinal: baseGastrointestinalSchema,
    genitourinary: baseGenitourinarySchema,
})
    .register(registry, { label: 'GI / GU' });
export const baseSnnMusculoskeletal = z
    .object({
    musculoskeletal: baseMusculoskeletalRefined,
})
    .register(registry, { label: 'Musculoskeletal' });
export const baseSnnHealthManagement = z
    .object({
    healthManagement: baseHealthManagementSchema,
})
    .register(registry, { label: 'Health Management' });
export const baseSnnSkinConditions = z
    .object({
    integumentary: baseIntegumentaryRefined,
})
    .register(registry, { label: 'Skin Conditions' });
export const baseSnnOstomy = z
    .object({
    ostomy: baseOstomySchemaRefined,
})
    .register(registry, { label: 'Ostomy' });
export const baseSnnLabdraw = z
    .object({
    labdraw: baseLabDrawSchema,
})
    .register(registry, { label: 'Lab Draw' });
export const snnValidationSchema = z.object({
    id: z.string().optional(),
    data: z.object({
        SubjectiveReport: baseSnnSubjectiveReport
            .extend({ patientPresentDuringVisit: patientPresentDuringVisitEnum.default('yes') })
            .superRefine((data, ctx) => {
            if ((data.changeStatus === 'worse' || data.changeStatus === 'emergency-visit') && !data.changeDetails?.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'required',
                    path: ['changeDetails'],
                });
            }
        }),
        ActiveDiagnoses: z.boolean().nullish(),
        HomeboundReason: baseHomeboundReason
            .extend({
            homeboundStatusThisVisit: zStringRequired(),
        })
            .superRefine((data, ctx) => {
            if (data.homeboundStatusThisVisit === 'changed' && !data.changeDescription?.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'required',
                    path: ['changeDescription'],
                });
            }
        }),
        Vitals: baseSnnVitals.extend({
            bloodPressure: baseBloodPressureRefined,
            pulse: basePulseRefined,
            respiration: baseRespirationRefined,
            temperature: baseTemperatureRefined,
            o2: baseO2Refined,
            pain: basePainRefined,
            bloodSugar: baseBloodSugarRefined,
        }),
        Nutrition: z.object({
            weightClinical: baseWeightClinicalRefined,
            oralIntakeClinical: baseOralIntakeClinical.refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            eatingSupport: baseEatingSupport.refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            pocNutrition: basePocNutrition,
            nutritionalApproaches: z.array(z.string()).optional(),
            nutritionalApproachesClinical: baseK0520Clinical.optional(),
        }),
        Neurosensory: baseNeurologicalSchemaRefined.extend({
            painAssessment: basePainAssessmentSchema,
            psychosocial: basePsychosocialSchemaRefined,
        }),
        Cardiovascular: z.object({
            cardiovascular: baseCardiovascularSchemaRefined,
        }),
        Respiratory: z.object({
            smokingHistory: baseSmokingHistorySchema,
            breathingPattern: baseBreathingPatternSchema,
            lungAuscultation: baseLungAuscultationSchemaRefined,
            cough: baseCoughSchema,
            cpapBipap: baseCpapBipapSchema,
            mechanicalVentilator: baseMechanicalVentilatorSchema,
            tracheostomy: baseTracheostomySchema,
            treatments: baseRespiratoryTreatmentsSchema,
        }),
        Endocrine: z.object({
            endocrine: baseEndocrineSchema.extend({ changeFromLastVisit: zStringRequired() }),
        }),
        BladderBowel: z.object({
            gastrointestinal: baseGastrointestinalSchema,
            genitourinary: baseGenitourinarySchema,
        }),
        Musculoskeletal: z.object({
            musculoskeletal: baseMusculoskeletalRefined,
        }),
        HealthManagement: z.object({
            healthManagement: baseHealthManagementSchema,
        }),
        SkinConditions: z.object({
            integumentary: baseIntegumentaryRefined,
        }),
        Ostomy: z.object({
            ostomy: baseOstomySchemaRefined,
        }),
        Labdraw: z.object({
            labdraw: baseLabDrawSchema,
        }),
        Infections: z.boolean().nullish(),
        Incidents: z.boolean().nullish(),
        Medication: z.boolean().nullish(),
        Supplies: z.boolean().nullish(),
        PlanOfCare: z.boolean().refine((value) => !!value, { message: 'section has errors' }),
        CoordinationOfCare: baseSnnCoordinationOfCare.extend({
            narrative: z
                .string()
                .optional()
                .refine((v) => v !== AI_NARRATIVE_PENDING_SENTINEL, {
                message: 'AI narrative is generating. Wait for it to finish before saving.',
            })
                .refine((v) => v !== AI_NARRATIVE_AWAITING_REVIEW_SENTINEL, {
                message: 'AI-generated narrative is awaiting review. Accept or discard before saving.',
            }),
        }),
    }),
    signedUserId: zStringRequired(),
    signedUserDate: zStringRequired(),
    meta: z
        .object({
        carryForward: z
            .object({
            sections: z.array(z.string()),
        })
            .optional(),
    })
        .nullish(),
});
